import { useEffect, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../stores/appStore';
import AdminLayout from '../components/admin/AdminLayout';

// Lazy load all tab components
const EventManagement = lazy(() => import('../components/admin/EventManagement'));
const CategoryManagement = lazy(() => import('../components/admin/CategoryManagement'));
const LocationManagement = lazy(() => import('../components/admin/LocationManagement'));
const ConnectorManagement = lazy(() => import('../components/admin/ConnectorManagement'));
const SignageOverview = lazy(() => import('../components/admin/SignageOverview'));
const SignageConfig = lazy(() => import('../components/admin/SignageConfig'));
const SignageDisplaySettings = lazy(() => import('../components/admin/SignageDisplaySettings'));
const SignageSlots = lazy(() => import('../components/admin/SignageSlots'));
const SignageThemes = lazy(() => import('../components/admin/SignageThemes'));
const SignageAlerts = lazy(() => import('../components/admin/SignageAlerts'));
const MediaLibrary = lazy(() => import('../components/admin/MediaLibrary'));
const MapManagement = lazy(() => import('../components/admin/MapManagement'));
const SocialWallConfig = lazy(() => import('../components/admin/SocialWallConfig'));
const StreamingManagement = lazy(() => import('../components/admin/StreamingManagement'));
const QuickLive = lazy(() => import('../components/admin/QuickLive'));
const UserManagement = lazy(() => import('../components/admin/UserManagement'));
const StaffManagement = lazy(() => import('../components/admin/StaffManagement'));
const NotificationManagement = lazy(() => import('../components/admin/NotificationManagement'));
const AdminDashboard = lazy(() => import('../components/admin/AdminDashboard'));
const SettingsManagement = lazy(() => import('../components/admin/SettingsManagement'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppStore();
  const activeTab = searchParams.get('tab') || 'events';

  // Auth check
  useEffect(() => {
    if (!user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
      navigate('/');
    }
  }, [user, navigate]);

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'events':
        return <Suspense fallback={<LoadingFallback />}><EventManagement /></Suspense>;
      case 'categories':
        return <Suspense fallback={<LoadingFallback />}><CategoryManagement /></Suspense>;
      case 'locations':
        return <Suspense fallback={<LoadingFallback />}><LocationManagement /></Suspense>;
      case 'connectors':
        return <Suspense fallback={<LoadingFallback />}><ConnectorManagement /></Suspense>;
      case 'signage-overview':
        return <Suspense fallback={<LoadingFallback />}><SignageOverview onNavigate={setActiveTab} /></Suspense>;
      case 'signage-config':
        return <Suspense fallback={<LoadingFallback />}><SignageConfig /></Suspense>;
      case 'signage-display':
        return <Suspense fallback={<LoadingFallback />}><SignageDisplaySettings /></Suspense>;
      case 'signage-slots':
        return <Suspense fallback={<LoadingFallback />}><SignageSlots /></Suspense>;
      case 'signage-themes':
        return <Suspense fallback={<LoadingFallback />}><SignageThemes /></Suspense>;
      case 'signage-alerts':
        return <Suspense fallback={<LoadingFallback />}><SignageAlerts /></Suspense>;
      case 'media':
        return <Suspense fallback={<LoadingFallback />}><MediaLibrary /></Suspense>;
      case 'map':
        return <Suspense fallback={<LoadingFallback />}><MapManagement /></Suspense>;
      case 'social':
        return <Suspense fallback={<LoadingFallback />}><SocialWallConfig /></Suspense>;
      case 'streaming':
        return <Suspense fallback={<LoadingFallback />}><StreamingManagement /></Suspense>;
      case 'live':
        return <Suspense fallback={<LoadingFallback />}><QuickLive /></Suspense>;
      case 'users':
        return <Suspense fallback={<LoadingFallback />}><UserManagement /></Suspense>;
      case 'staff':
        return <Suspense fallback={<LoadingFallback />}><StaffManagement /></Suspense>;
      case 'notifications':
        return <Suspense fallback={<LoadingFallback />}><NotificationManagement /></Suspense>;
      case 'dashboard':
        return <Suspense fallback={<LoadingFallback />}><AdminDashboard /></Suspense>;
      case 'settings':
        return <Suspense fallback={<LoadingFallback />}><SettingsManagement /></Suspense>;
      default:
        return <Suspense fallback={<LoadingFallback />}><EventManagement /></Suspense>;
    }
  };

  if (!user) return null;

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}
    </AdminLayout>
  );
};

export default AdminPage;
